import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Wrapper from '../components/Wrapper';
import Api from '../Api';
import { Helmet } from 'react-helmet';
import { amo, bitrix, crm, global, historyHeader, input, sip } from '../translate';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    InputAdornment,
    FormControlLabel,
    Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { userCrmRequest } from '../store/actions/users';
import { greenTheme } from '../helpers/style';

const { REACT_APP_SERVER } = process.env;
const primary = REACT_APP_SERVER === 'ATS' ? '#0973b9' : greenTheme.primary;
let newArr = [];

const cardPaperSx = {
    p: 2.5,
    borderRadius: 3,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.06)',
};

const customStyles = {
    menu: (provided) => ({ ...provided, position: 'absolute', zIndex: 9999, maxHeight: 300 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base, state) => ({
        ...base,
        borderRadius: 10,
        minHeight: 40,
        borderColor: state.isFocused ? primary : 'rgba(0,0,0,0.23)',
        boxShadow: state.isFocused ? `0 0 0 2px ${primary}40` : base.boxShadow,
        '&:hover': { borderColor: primary },
    }),
};

function Pipedrive() {
    const lang = +localStorage.getItem('atsLang') || 1;
    const userData = useSelector((state) => state.users.crmData);
    const [pipedrive, setPipedrive] = useState([]);
    const [loader, setLoader] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sipNum, setSipNum] = useState([]);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        (async () => {
            try {
                setLoader(true);
                const { data } = await Api.pipedriveUsers();
                const users = (data.data || []).map((u) => ({
                    ...u,
                    sips: (u.sips || []).map((s) => {
                        let isCall = !!s.is_call;
                        if (!isCall && s?.params && typeof s.params === 'string') {
                            isCall = new URLSearchParams(s.params).get('is_call') === '1';
                        }
                        return { ...s, is_call: isCall };
                    }),
                }));
                setPipedrive(users);
                newArr = users.map((u) => ({ userid: u.id, sip: u.sips || [] }));
                setLoader(false);
            } catch (e) {
                navigate('/crm_integration');
            }
        })();
    }, [navigate]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await Api.getAllowed();
                setSipNum(data.inputNumber?.sip || []);
            } catch (e) {
                console.log(e);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            await dispatch(userCrmRequest());
        })();
    }, [dispatch]);

    const handleSelectUser = useCallback((user) => {
        setSelectedUser(user);
    }, []);

    const handleAddSip = useCallback(
        (sipOption) => {
            if (!sipOption?.value || !selectedUser) return;
            const sip = sipNum.find((k) => +k.number === sipOption.value);
            setPipedrive((prev) =>
                prev.map((u) => {
                    if (u.id !== selectedUser.id) return u;
                    const existing = u.sips || [];
                    if (existing.some((s) => +s.sip === +sipOption.value)) return u;
                    return {
                        ...u,
                        sips: [...existing, { sip: sipOption.value, id: null, is_call: false }],
                    };
                })
            );
        },
        [selectedUser, sipNum]
    );

    const handleIsCallToggle = useCallback((userId, sipItem, checked) => {
        setPipedrive((prev) =>
            prev.map((u) => {
                if (u.id !== userId) return u;
                const sips = (u.sips || []).map((s) =>
                    String(s.sip) === String(sipItem.sip) ? { ...s, is_call: !!checked } : s
                );
                return { ...u, sips };
            })
        );
    }, []);

    const handleRemoveSip = useCallback((user, sipItem) => {
        setPipedrive((prev) =>
            prev.map((u) => {
                if (u.id !== user.id) return u;
                const sips = (u.sips || []).filter((s) => s !== sipItem && (s.id !== sipItem.id || s.sip !== sipItem.sip));
                return { ...u, sips };
            })
        );
    }, []);

    const handleSipSelect = useCallback((sipArr, id) => {
        setPipedrive((prev) =>
            prev.map((u) => {
                if (u.id !== id) return u;
                return {
                    ...u,
                    sips: (sipArr || []).map((s) => ({ sip: s.value, id: s.my_id })),
                };
            })
        );
    }, []);

    const handleSave = useCallback(
        async (item) => {
            try {
                const f = newArr.find((s) => s.userid === item.id);
                const prevSips = f?.sip || [];

                for (const prev of prevSips) {
                    if (prev.id) {
                        await Api.pipedriveCrmDelete(prev.id);
                    }
                }

                const sips = [];
                if (item.sips?.length) {
                    for (const sipItem of item.sips) {
                        const res = await Api.pipedriveCrmCreate({
                            userid: item.id,
                            sip: sipItem.sip,
                            domain: userData?.pipedrive?.domain?.split('.')[0] || '',
                            is_call: !!sipItem.is_call,
                        });
                        sips.push(res.data?.crm);
                    }
                }

                const { data } = await Api.pipedriveUsers();
                const users = (data.data || []).map((u) => ({
                    ...u,
                    sips: (u.sips || []).map((s) => {
                        let isCall = !!s.is_call;
                        if (!isCall && s?.params && typeof s.params === 'string') {
                            isCall = new URLSearchParams(s.params).get('is_call') === '1';
                        }
                        return { ...s, is_call: isCall };
                    }),
                }));
                setPipedrive(users);
                newArr = users.map((u) => ({ userid: u.id, sip: u.sips || [] }));

                if (prevSips.length && item.sips?.length) {
                    toast.success('Successfully edited');
                } else if (!prevSips.length && item.sips?.length) {
                    toast.success('Successfully created');
                } else if (prevSips.length && !item.sips?.length) {
                    toast.success('Successfully deleted');
                }
            } catch (e) {
                toast.error(e?.response?.data?.message || 'Error');
            }
        },
        [userData]
    );

    const selectedUserData = useMemo(() => {
        if (!selectedUser) return null;
        return pipedrive.find((r) => r.id === selectedUser.id) || selectedUser;
    }, [selectedUser, pipedrive]);

    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) return pipedrive;
        const q = searchQuery.toLowerCase();
        return pipedrive.filter(
            (s) =>
                `${s.name || ''} ${s.email || ''} ${s.phone || ''}`.toLowerCase().includes(q)
        );
    }, [pipedrive, searchQuery]);

    const sipOptions = useMemo(
        () =>
            [{ value: '', label: input.select[lang] }, ...sipNum.map((k) => ({ value: +k.number, label: `${k.number} - ${k.context || ''}` }))],
        [sipNum, lang]
    );

    return (
        <>
            <Helmet>
                <title>{sip.crm[lang]}</title>
            </Helmet>
            <Wrapper>
                <div className="tableParent">
                    {loader ? (
                        <div className="loaderContainer">
                            <Loader />
                        </div>
                    ) : (
                        <div
                            className="tableParent"
                            id="tableParent"
                            style={{ display: 'flex', gap: 24, overflow: 'hidden', minWidth: 0 }}
                        >
                            {/* User list - left panel */}
                            <Paper sx={{ ...cardPaperSx, flex: selectedUser ? 1.5 : 1, minWidth: 0, minHeight: 0 }}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: 'text.primary' }}>
                                    {bitrix.user_config[lang]} – {userData?.pipedrive?.domain || 'Pipedrive'}
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={bitrix.search_users[lang]}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: 'text.secondary' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                                <TableContainer sx={{ maxHeight: 420 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                                                    {bitrix.bitrix_user[lang]}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                                                    {bitrix.mapped_to[lang]}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: 80 }}>
                                                    {historyHeader.status[lang]}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredUsers.map((s) => {
                                                const isSelected = selectedUser?.id === s.id;
                                                const sipCount = (s.sips || []).length;
                                                return (
                                                    <TableRow
                                                        key={s.id}
                                                        hover
                                                        selected={isSelected}
                                                        onClick={() => handleSelectUser(s)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            bgcolor: isSelected ? `${primary}12` : undefined,
                                                        }}
                                                    >
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                <Avatar
                                                                    sx={{
                                                                        width: 36,
                                                                        height: 36,
                                                                        bgcolor: primary,
                                                                        fontSize: '0.875rem',
                                                                    }}
                                                                >
                                                                    {(s.name?.[0] || '') + (s.name?.[1] || '')}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight={600}>
                                                                        {s.name}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {s.email}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {sipCount > 0
                                                                    ? `${sipCount} SIP(s): ${(s.sips || [])
                                                                          .map(
                                                                              (sp) =>
                                                                                  `${sp.sip}${sipNum.find((k) => +k.number === sp.sip)?.context ? ` (${sipNum.find((k) => +k.number === sp.sip).context})` : ''}`
                                                                          )
                                                                          .join(', ')}`
                                                                    : bitrix.not_assigned[lang]}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {s?.rights?.is_active ? (
                                                                <CheckCircleIcon sx={{ color: primary, fontSize: 20 }} />
                                                            ) : (
                                                                <RadioButtonUncheckedIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {filteredUsers.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                        {bitrix.no_users_found[lang]}
                                    </Typography>
                                )}
                            </Paper>

                            {/* Right panel: Config - when user selected */}
                            {selectedUser && selectedUserData && (
                                <Paper sx={{ ...cardPaperSx, flex: 1, minWidth: 0, minHeight: 0, position: 'relative' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                                        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                                            {bitrix.config_for[lang]} {selectedUserData.name}
                                        </Typography>
                                        <IconButton size="small" onClick={() => setSelectedUser(null)} sx={{ ml: 1 }} aria-label="Close">
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.secondary' }}>
                                                {bitrix.user_mapping[lang]}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                {(selectedUserData.sips || []).map((sipItem, idx) => (
                                                    <Box
                                                        key={idx}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5,
                                                            p: 1.5,
                                                            borderRadius: 2,
                                                            border: '1px solid rgba(0,0,0,0.08)',
                                                            backgroundColor: 'rgba(0,0,0,0.02)',
                                                        }}
                                                    >
                                                        <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
                                                            {sipItem.sip} – {sipNum.find((k) => +k.number === sipItem.sip)?.context || ''}
                                                        </Typography>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    size="small"
                                                                    checked={!!sipItem.is_call}
                                                                    onChange={(e) =>
                                                                        handleIsCallToggle(selectedUserData.id, sipItem, e.target.checked)
                                                                    }
                                                                    sx={{
                                                                        '& .MuiSwitch-switchBase.Mui-checked': { color: primary },
                                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: primary },
                                                                    }}
                                                                />
                                                            }
                                                            label={<Typography variant="caption">Is Call</Typography>}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveSip(selectedUserData, sipItem)}
                                                            sx={{ color: '#ef4444' }}
                                                            aria-label="Remove"
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                                <Box sx={{ minWidth: 200 }}>
                                                    <Select
                                                        styles={customStyles}
                                                        menuPlacement="auto"
                                                        menuPortalTarget={document.body}
                                                        onChange={(ev) => handleAddSip(ev)}
                                                        value={null}
                                                        options={sipOptions.filter(
                                                            (o) =>
                                                                o.value === '' ||
                                                                !(selectedUserData.sips || []).some((s) => +s.sip === +o.value)
                                                        )}
                                                        isSearchable
                                                        placeholder={`${global.add[lang]} SIP...`}
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 1.5, pt: 1 }}>
                                            <Button
                                                variant="contained"
                                                size="medium"
                                                sx={{
                                                    bgcolor: primary,
                                                    '&:hover': { bgcolor: primary },
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                }}
                                                onClick={() => handleSave(selectedUserData)}
                                            >
                                                {global.edit[lang]}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            )}
                        </div>
                    )}
                </div>

                <div className="tableParent" style={{ marginTop: 24 }}>
                    <Paper sx={{ ...cardPaperSx, p: 2.5 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: 'text.primary' }}>
                            {crm.config[lang]}
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
                            <li>
                                <Typography variant="body2">{bitrix.card[lang]}</Typography>
                            </li>
                            <li>
                                <Typography variant="body2">{amo.record[lang]}</Typography>
                            </li>
                        </Box>
                    </Paper>
                </div>
            </Wrapper>
        </>
    );
}

export default Pipedrive;
